import {
  Box,
  FieldSelect,
  FlexBox,
  Switch,
  Text,
} from '@dixa/lib-shared-habla';
import { colors, sizes } from '@dixa/lib-shared-tokens';
import { ChangeEvent, useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { flowsApi } from 'apis/flows';

type Props = {
  clearable?: boolean;
  onChange: (countryCode: null | string, isClosed: boolean) => void;
};

export const HolidayCalendarSelector = ({
  clearable,
  onChange,
}: Props): JSX.Element => {
  const [isClosed, setIsClosed] = useState(false);
  const [countryCode, setCountryCode] = useState<null | string>(null);

  const { countries } = flowsApi.useReadFlowsSchedulesCountriesQuery(
    undefined,
    {
      selectFromResult: ({ data }) => {
        const countries = [...(data?.data?.countries ?? [])];

        return {
          countries: countries.sort(({ name: aName }, { name: bName }) =>
            aName.localeCompare(bName),
          ),
        };
      },
    },
  );

  useUpdateEffect(() => {
    onChange(countryCode, isClosed);
  }, [countryCode, isClosed]);

  const handleSwitchChange = useCallback(
    ({
      target: { checked: isClosed },
    }: ChangeEvent<HTMLInputElement>): void => {
      setIsClosed(isClosed);
    },
    [],
  );

  return (
    <Box>
      <FieldSelect
        input={{
          name: 'holiday-calendar',
          value: countryCode ?? '',
          onChange: (countryCode) => setCountryCode(countryCode ?? null),
        }}
        items={countries.map(({ country_code, name }) => ({
          id: country_code,
          name,
        }))}
        label="Holiday calendar"
        noAlert
        placeholder="Connect holiday calendar with this schedule"
        {...(clearable && {
          clearable: true,
          onClear: () => setCountryCode(null),
        })}
        searchable
      />
      {!!countryCode && (
        <FlexBox
          alignItems="center"
          background={colors.neutral['100']}
          borderRadius={sizes['border-radius']['100']}
          justifyContent="space-between"
          margin="250 0 0"
          padding="300"
        >
          <Text.Paragraph>
            Set business hours to &quot;closed&quot; on holidays.
          </Text.Paragraph>
          <Switch
            name="is-closed"
            value="is-closed"
            checked={isClosed}
            onChange={handleSwitchChange}
          />
        </FlexBox>
      )}
    </Box>
  );
};
